import { Button } from '@heroui/react'
import { useCurrentApiKey, useDeleteCurrentApiKey, useGenerateApiKey } from '@qse/api'
import { queryKeys } from '@qse/api'
import { ModelControlEnum } from '@qse/constants'
import { useActiveModelStore } from '@qse/stores'
import { useQueryClient } from '@tanstack/react-query'

export const APIKeyManagment = () => {
  const { data: currentKey, isLoading } = useCurrentApiKey()
  const queryClient = useQueryClient()
  const deleteApiKeyMutation = useDeleteCurrentApiKey()
  const generateApiKeyMutation = useGenerateApiKey()

  const handleGenerateApiKey = async () => {
    try {
      const result = await generateApiKeyMutation.mutateAsync()

      // Update cache to reflect the new API key
      queryClient.setQueryData(queryKeys.apiKeys.current(), {
        exists: true,
        displayKey:
          result.key.substring(0, 8) + '...' + result.key.substring(result.key.length - 6),
        createdAt: new Date().toISOString(),
      })

      // Store the generated key for display
      const config = {
        modalHeader: {
          title: 'API Key Generated',
          showCloseButton: true,
        },
        data: {
          key: result.key,
          warning: result.warning,
          displayKey: result.key,
        },
      }

      useActiveModelStore.setState({
        data: config,
        width: 'max-w-[680px]',
        modelActive: ModelControlEnum.API_KEY_DETAILS,
        toggleModel: true,
        scrollBehavior: 'inside',
      })
    } catch (error) {
      // Error handling is now managed by the hook
    }
  }

  const handleDeleteApiKey = async () => {
    try {
      await deleteApiKeyMutation.mutateAsync()

      // Update cache to reflect the deleted API key
      queryClient.setQueryData(queryKeys.apiKeys.current(), {
        exists: false,
        displayKey: undefined,
        createdAt: undefined,
      })
    } catch (error) {
      // Error handling is now managed by the hook
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <p className="font-medium text-[20px]">API Key Management</p>
      {isLoading ? (
        <div>Loading...</div>
      ) : currentKey?.exists ? (
        <div
          className="flex flex-1 items-center justify-between gap-2 flex-wrap"
          style={{ opacity: deleteApiKeyMutation.isPending ? 0.5 : 1 }}
        >
          <div className="flex flex-col gap-1">
            <p className="font-medium">{currentKey.displayKey}</p>
            {currentKey.createdAt && (
              <p className="text-sm text-gray-500">
                Created at: {new Date(currentKey.createdAt).toLocaleDateString()}
              </p>
            )}
          </div>
          <div className="flex gap-4">
            <Button
              color="primary"
              disabled={deleteApiKeyMutation.isPending}
              isLoading={generateApiKeyMutation.isPending}
              radius="sm"
              size="md"
              onPress={handleGenerateApiKey}
            >
              Generate New Key
            </Button>
            <Button
              color="danger"
              disabled={generateApiKeyMutation.isPending}
              isLoading={deleteApiKeyMutation.isPending}
              radius="sm"
              size="md"
              variant="bordered"
              onPress={handleDeleteApiKey}
            >
              Delete Key
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex justify-between items-center">
          <div className="italic text-sm text-gray-500">No API key generated</div>
          <Button
            color="primary"
            isLoading={generateApiKeyMutation.isPending}
            radius="sm"
            size="md"
            onPress={handleGenerateApiKey}
          >
            Generate API Key
          </Button>
        </div>
      )}
    </div>
  )
}
